<template>
<div>
    <section v-if="lines.length" role="alert" aria-live="polite" class="timesheet-unresolved-blocker">
        <div class="timesheet-unresolved-blocker__title">
            <i class="fas fa-user-minus" aria-hidden="true"></i>
            <div><strong>ТАБЕЛЬ НЕЛЬЗЯ ОТПРАВИТЬ: пустых строк — {{ lines.length }}</strong>
                <span>У сотрудников из списка нет часов, кодов дней, комментариев, КТУ и характеристики.</span>
                <span>Если сотрудник должен остаться в табеле — заполните фактические данные. Лишние строки уберите, затем повторите отправку.</span>
            </div>
        </div>
        <div class="timesheet-unresolved-blocker__people">
            <article><div><strong>Проверьте список перед удалением</strong><span>Вы сможете выбрать строки и указать общую причину. Удаление потребует подтверждения.</span></div>
                <b-button v-if="canEdit" variant="danger" class="timesheet-unresolved-attention" v-timesheet-attention :disabled="busy" @click="open"><i class="fas fa-list-ul mr-1" aria-hidden="true"></i>Проверить и убрать пустые строки</b-button>
            </article>
        </div>
        <details class="mt-2"><summary>Показать сотрудников — {{ lines.length }}</summary>
            <div class="timesheet-unresolved-blocker__people"><article v-for="line in lines" :key="line.line_id">
                <EmployeeLink :employee-id="line.employee_id" :fallback="line.full_name" open-in-panel />
                <b-button size="sm" variant="outline-danger" @click="$emit('locate', line.line_id)">К строке</b-button>
            </article></div>
        </details>
    </section>
    <div v-if="error && !visible" role="alert" class="alert alert-danger">{{ error }}</div>
    <b-modal v-model="visible" title="Проверить и убрать пустые строки" scrollable centered :no-close-on-backdrop="busy" :no-close-on-esc="busy" :hide-header-close="busy" @hide="preventClose">
        <p><strong>{{ sheet.object.name }} · {{ sheet.timesheet.name || 'Табель' }} · {{ sheet.period.starts_on.slice(0, 7) }}</strong></p>
        <p>Будут убраны только выбранные строки из этого табеля. Вернуть сотрудника можно через «Добавить сотрудника». Табель не будет отправлен автоматически.</p>
        <div ref="selectionFields" tabindex="-1">
        <div v-for="line in candidates" :key="line.line_id" class="mb-3">
            <b-form-checkbox v-model="selected" :value="line.line_id" :disabled="busy">Выбрать строку</b-form-checkbox>
            <EmployeeLink :employee-id="line.employee_id" :fallback="line.full_name" open-in-panel />
            <small v-if="source(line).exclusion_has_assignment" class="d-block">Причина также попадёт в характеристики: сотрудник назначен на объект.</small>
            <small v-for="day in (source(line).absence_days || []).filter(d => d.state === 'suggestion')" :key="day.work_date" class="d-block">Кадровое отсутствие: {{ day.work_date }} · {{ day.message || day.code || 'Есть предложение кадровой отметки' }}</small>
        </div>
        </div>
        <b-form-group label="Общая причина для выбранных сотрудников" :label-for="'empty-reason-' + _uid">
            <b-form-select ref="reasonField" :id="'empty-reason-' + _uid" v-model="reasonCode" :disabled="busy" :options="reasons" :state="validationAttempted && !hasReason ? false : null" />
            <b-form-invalid-feedback :state="validationAttempted && !hasReason ? false : null">Выберите причину из списка или напишите свою ниже.</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Своя причина или дополнение"><AppTextarea v-model="reasonText" :disabled="busy" :maxlength="2000" /></b-form-group>
        <blockquote v-if="reasonText.trim()" class="user-quote">{{ reasonText }}</blockquote>
        <template #modal-footer><div class="w-100">
            <div v-if="validationMessage" role="alert" class="alert alert-danger py-2">{{ validationMessage }}</div>
            <div v-if="error" role="alert" class="alert alert-danger">{{ error }} <b-button v-if="!done" size="sm" :disabled="busy" @click="refreshCandidates">Обновить список</b-button></div>
            <div v-if="done" role="status" class="alert alert-success">Выбранные строки убраны. Проверьте табель и повторите отправку.</div>
            <div class="d-flex flex-wrap justify-content-end">
                <b-button :disabled="busy" @click="visible = false">{{ done ? 'Закрыть' : 'Отмена' }}</b-button>
                <b-button v-if="!done" variant="danger" class="ml-2" :disabled="busy" :class="{'empty-lines-incomplete': !selected.length || !hasReason}" @click="remove">Убрать выбранные строки — {{ selected.length }}</b-button>
                <b-button v-else :disabled="busy" class="ml-2" @click="refresh">Обновить табель</b-button>
            </div>
        </div></template>
    </b-modal>
</div>
</template>
<script>
import timesheetAttention from './timesheetAttention';
import api from '@/utils/api';
import EmployeeLink from '@/components/EmployeeLink.vue';
export default {
    components: {EmployeeLink},
    directives: {timesheetAttention},
    props: {sheet: Object, lines: Array, canEdit: Boolean, prepare: Function, reload: Function},
    data: () => ({validationAttempted: false, visible: false, busy: false, error: '', candidates: [], selected: [], reasonCode: '', reasonText: '', operation: '', done: false,
        reasons: [{value: '', text: 'Выберите причину или напишите свою'}, {value: 'not_arrived', text: 'Не приехал'}, {value: 'added_by_mistake', text: 'Ошибочно добавлен'}, {value: 'no_work_this_month', text: 'Не работал на объекте в этом месяце'}, {value: 'left_object', text: 'Выбыл с объекта'}]}),
    computed: {
        hasReason() { return Boolean(this.reasonCode || this.reasonText.trim()); },
        validationMessage() {
            if (!this.validationAttempted) return '';
            if (!this.selected.length) return 'Выберите хотя бы одну строку для удаления.';
            return this.hasReason ? '' : 'Укажите общую причину: выберите из списка или напишите свою.';
        }
    },
    beforeDestroy() { this.disposed = true; this.$emit('busy', false); },
    watch: {
        busy() { this.$emit('busy', this.busy || this.visible); },
        visible() { this.$emit('busy', this.busy || this.visible); },
        selected() { if (!this.busy) this.operation = ''; },
        reasonCode() { if (!this.busy) this.operation = ''; },
        reasonText() { if (!this.busy) this.operation = ''; }
    },
    methods: {
        async refreshCandidates() {
            this.busy = true;
            try {
                if (!await this.prepare()) throw Error('Сначала сохраните изменения и исправьте ошибки сохранения.');
                if (this.disposed) return;
                const response = await api.get(`/api/timesheets/${this.sheet.timesheet.id}/empty-lines-preview`, {ignoreModal: true});
                if (this.disposed) return;
                this.candidates = response.data.lines; this.selected = this.selected.filter(id => this.candidates.some(l => l.line_id === id));
                this.operation = ''; this.$emit('checked', response.data); this.error = 'Список обновлён. Проверьте выбор перед подтверждением.';
            } catch (e) { if (!this.disposed) this.error = e.message; } finally { this.busy = false; }
        },
        source(line) { return this.sheet.lines.find(l => Number(l.id) === line.line_id) || {}; },
        preventClose(event) { if (this.busy) event.preventDefault(); },
        async open() {
            this.validationAttempted = false; this.error = ''; this.busy = true;
            try {
                if (!await this.prepare()) throw Error('Сначала сохраните изменения и исправьте ошибки сохранения.');
                if (this.disposed) return;
                const response = await api.get(`/api/timesheets/${this.sheet.timesheet.id}/empty-lines-preview`, {ignoreModal: true});
                if (this.disposed) return;
                this.candidates = response.data.lines; this.selected = this.candidates.map(l => l.line_id);
                this.$emit('checked', response.data); this.done = false; this.reasonCode = ''; this.reasonText = '';
                this.operation = ''; this.visible = true;
            } catch (e) { this.error = e.response && e.response.data && e.response.data.message || e.message; }
            finally { this.busy = false; }
        },
        async refresh() { this.busy = true; try { await this.reload(); this.visible = false; } catch (_) { this.error = 'Строки убраны. Не удалось обновить табель — повторите обновление.'; } finally { this.busy = false; } },
        async remove() {
            if (this.busy || this.done) return;
            this.validationAttempted = true;
            if (!this.selected.length || !this.hasReason) {
                await this.$nextTick();
                if (this.disposed) return;
                const ref = this.selected.length ? this.$refs.reasonField : this.$refs.selectionFields;
                const container = ref && (ref.$el || ref);
                const target = this.selected.length ? container : container && (container.querySelector('input') || container);
                if (target) { target.focus({preventScroll: true}); target.scrollIntoView({behavior: 'smooth', block: 'center'}); }
                return;
            }
            this.busy = true; this.error = '';
            try {
                if (!await this.prepare()) throw Error('Сначала сохраните изменения и обновите список.');
                if (this.disposed) return;
                if (!this.operation) this.operation = window.crypto.randomUUID();
                await api.post(`/api/timesheets/${this.sheet.timesheet.id}/empty-lines/exclude`, {operation_id: this.operation,
                    lines: this.candidates.filter(l => this.selected.includes(l.line_id)).map(l => ({line_id: l.line_id, token: l.token})), reason_code: this.reasonCode || null, reason_text: this.reasonText}, {ignoreModal: true});
                if (this.disposed) return;
                this.done = true; await this.refresh();
            } catch (e) { this.error = e.response && e.response.data && e.response.data.message || e.message; }
            finally { this.busy = false; }
        }
    }
};
</script>

<style scoped>
.empty-lines-incomplete:not(:disabled) { opacity: .65; }
.empty-lines-incomplete:hover, .empty-lines-incomplete:focus { opacity: 1; }
</style>
